import { useEffect, useState } from "react";

export default function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installError, setInstallError] = useState("");
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem("pwaInstallDismissed") === "1",
  );

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      const dismissed = localStorage.getItem("pwaInstallDismissed") === "1";
      const installed = localStorage.getItem("pwaInstalled") === "1";
      if (dismissed || installed) {
        setIsDismissed(dismissed);
        setIsInstallable(false);
        return;
      }
      event.preventDefault();
      setDeferredPrompt(event);
      setIsInstallable(true);
      setInstallError("");
    };

    const handleAppInstalled = () => {
      localStorage.setItem("pwaInstallDismissed", "1");
      localStorage.setItem("pwaInstalled", "1");
      setIsDismissed(true);
      setDeferredPrompt(null);
      setIsInstallable(false);
      setInstallError("");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt || typeof deferredPrompt.prompt !== "function") {
      setInstallError(
        "Install prompt isn't available. Please use the browser menu to install.",
      );
      return;
    }

    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome !== "accepted") {
        setInstallError(
          "Install was dismissed. You can install anytime from the browser menu.",
        );
      } else {
        localStorage.setItem("pwaInstallDismissed", "1");
        localStorage.setItem("pwaInstalled", "1");
        setIsDismissed(true);
      }
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      setInstallError(
        "We couldn't open the install dialog. Please use the browser menu to install.",
      );
    }
  };

  const dismissInstall = () => {
    localStorage.setItem("pwaInstallDismissed", "1");
    setIsDismissed(true);
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const clearInstallError = () => setInstallError("");

  return {
    isInstallable,
    isDismissed,
    promptInstall,
    dismissInstall,
    installError,
    clearInstallError,
  };
}
